/**
 * The pure half of lib/alerts.ts (Settings, Alerts in Admin, Warren, 29 Oct 2026): the preview's shapes and the
 * helpers the page uses, free of the Api client so the tests can load them.
 */
export const PREVIEW_TYPES = ['down', 'site_down', 'back_online', 'network_event', 'site_digest', 'ops_morning', 'still_offline', 'unstable_note', 'refused_start'] as const;
export type PreviewType = (typeof PREVIEW_TYPES)[number];
export type PreviewClass = 'building' | 'home' | 'evi_ops';
export interface PreviewCountsView {
  total: number;
  by_type: Record<PreviewType, number>;
  by_class: Record<PreviewClass, { emails: number; recipients: number; max_per_recipient_day: number }>;
  by_day: Array<{ day: string } & Record<PreviewClass, number>>;
}
export interface AlertPreview {
  window: { from: string; to: string; days: number; coverage_from: string | null; partial: boolean };
  proposed: PreviewCountsView;
  current: PreviewCountsView;
  actual: { total: number; by_type: Record<PreviewType, number>; exact_types: PreviewType[]; approximate_types: PreviewType[] };
  delta: { total: number; by_type: Record<PreviewType, number> };
  top_chargers: Array<{ identity: string; site: string | null; proposed: number; current: number }>;
  notes: string[];
}

/** A textarea of addresses: one per line, or separated by commas, semicolons or spaces; blanks dropped. */
export function addressList(text: string): string[] {
  return text.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
}

export const PREVIEW_TYPE_LABELS: Readonly<Record<PreviewType, string>> = {
  down: 'Charger down',
  site_down: 'Whole site down',
  back_online: 'Back online',
  network_event: 'Network event (EVI ops)',
  site_digest: 'Morning summary of a site',
  ops_morning: 'Morning email to EVI ops',
  still_offline: 'Still offline (kept choices)',
  unstable_note: 'Network checklist',
  refused_start: 'Refused starts',
};

/** One row per email type: the saved settings, the edited ones, what the records say went out, and whether that is approximate. */
export function previewRows(p: AlertPreview): Array<{ type: PreviewType; label: string; current: number; proposed: number; actual: number; approximate: boolean }> {
  return PREVIEW_TYPES.map((type) => ({
    type, label: PREVIEW_TYPE_LABELS[type], current: p.current.by_type[type] ?? 0, proposed: p.proposed.by_type[type] ?? 0,
    actual: p.actual.by_type[type] ?? 0, approximate: p.actual.approximate_types.includes(type),
  }));
}
